import React, { useCallback, useEffect, useState } from 'react';
import Box from '@amzn/awsui-components-console/box';
import Button from '@amzn/awsui-components-console/button';
import ColumnLayout from '@amzn/awsui-components-console/column-layout';
import FormField from '@amzn/awsui-components-console/form-field';
import Input from '@amzn/awsui-components-console/input';
import Modal from '@amzn/awsui-components-console/modal';
import RadioGroup from '@amzn/awsui-components-console/radio-group';
import SpaceBetween from '@amzn/awsui-components-console/space-between';
import Textarea from '@amzn/awsui-components-console/textarea';
import {
  DraftStringInput,
  InlineMarkupType,
  MessageFormatType,
  StringInfo,
} from '../types';
import {
  CONTEXT_NOTE_CHAR_LIMIT,
  SOURCE_TEXT_CHAR_LIMIT,
  STRING_ID_FORMAT_ERROR,
  STRING_ID_MAX_LENGTH,
  STRING_ID_MAX_LENGTH_ERROR,
  STRING_ID_PATTERN,
} from '../constants';

interface EditStringModalProps {
  visible: boolean;
  stringInfo: StringInfo | null;
  sourceLocale: string;
  /** Whether this string is a draft-added string (not yet on the server). */
  isDraftAdded?: boolean;
  /** IDs of strings already committed on the server. Used for uniqueness validation. */
  existingStringIds?: string[];
  /** IDs of other draft-added strings. Used for uniqueness validation. */
  draftAddedStringIds?: string[];
  onSave: (draft: DraftStringInput, originalStringId?: string) => void;
  onDismiss: () => void;
}

interface FormErrors {
  stringId?: string;
  sourceText?: string;
  contextNote?: string;
}

const EditStringModal: React.FC<EditStringModalProps> = ({
  visible,
  stringInfo,
  sourceLocale,
  isDraftAdded = false,
  existingStringIds = [],
  draftAddedStringIds = [],
  onSave,
  onDismiss,
}) => {
  const [stringId, setStringId] = useState('');
  const [sourceText, setSourceText] = useState('');
  const [contextNote, setContextNote] = useState('');
  const [inlineMarkupType, setInlineMarkupType] = useState<InlineMarkupType>(
    InlineMarkupType.PlainText,
  );
  const [messageFormatType, setMessageFormatType] = useState<MessageFormatType>(
    MessageFormatType.None,
  );
  const [errors, setErrors] = useState<FormErrors>({});

  // Populate the form whenever the modal opens or targets a different string.
  // `visible` is a dependency because the parent keeps this component mounted
  // between opens: without it, reopening for the same `stringInfo` object would
  // skip the effect and show the previous session's unsaved edits.
  useEffect(() => {
    if (visible && stringInfo) {
      setStringId(stringInfo.stringId);
      setSourceText(stringInfo.sourceText);
      setContextNote(stringInfo.contextNote ?? '');
      setInlineMarkupType(
        stringInfo.inlineMarkupType ?? InlineMarkupType.PlainText,
      );
      setMessageFormatType(
        stringInfo.messageFormatType ?? MessageFormatType.None,
      );
      setErrors({});
    }
  }, [visible, stringInfo]);

  const handleDismiss = useCallback(() => {
    setErrors({});
    onDismiss();
  }, [onDismiss]);

  const validate = (): boolean => {
    const newErrors: FormErrors = {};
    // Validate the trimmed values because handleSave submits trimmed values,
    // keeping this flow's rules identical to CreateStringModal's.
    const trimmedStringId = stringId.trim();
    const trimmedSourceText = sourceText.trim();
    const trimmedContextNote = contextNote.trim();

    // Validate string ID format when editable (draft-added strings only)
    if (isDraftAdded) {
      if (!trimmedStringId) {
        newErrors.stringId = 'String ID is required.';
      } else if (!STRING_ID_PATTERN.test(trimmedStringId)) {
        newErrors.stringId = STRING_ID_FORMAT_ERROR;
      } else if (trimmedStringId.length > STRING_ID_MAX_LENGTH) {
        newErrors.stringId = STRING_ID_MAX_LENGTH_ERROR;
      } else if (trimmedStringId !== stringInfo?.stringId) {
        // Only check uniqueness when the ID was actually changed
        const allIds = [...existingStringIds, ...draftAddedStringIds];
        // Exclude the original ID (the string being edited) from the check
        const otherIds = allIds.filter((id) => id !== stringInfo?.stringId);
        if (otherIds.includes(trimmedStringId)) {
          newErrors.stringId = 'A string with this ID already exists.';
        }
      }
    }

    if (!trimmedSourceText) {
      newErrors.sourceText = 'Source text is required.';
    } else if (trimmedSourceText.length > SOURCE_TEXT_CHAR_LIMIT) {
      newErrors.sourceText = `Source text exceeds ${SOURCE_TEXT_CHAR_LIMIT} character limit.`;
    }

    if (!trimmedContextNote) {
      newErrors.contextNote = 'Context note is required.';
    } else if (trimmedContextNote.length > CONTEXT_NOTE_CHAR_LIMIT) {
      newErrors.contextNote = `Context note exceeds ${CONTEXT_NOTE_CHAR_LIMIT} character limit.`;
    }

    setErrors(newErrors);
    return (
      !newErrors.stringId && !newErrors.sourceText && !newErrors.contextNote
    );
  };

  const handleSave = () => {
    if (!stringInfo || !validate()) {
      return;
    }

    const trimmedStringId = isDraftAdded
      ? stringId.trim()
      : stringInfo.stringId;
    const originalStringId = stringInfo.stringId;

    const draft: DraftStringInput = {
      stringId: trimmedStringId,
      sourceLocale,
      sourceText: sourceText.trim(),
      contextNote: contextNote.trim(),
      inlineMarkupType,
      messageFormatType,
      variants: stringInfo.variants,
    };

    // Pass originalStringId so the caller can handle renames for draft-added strings
    onSave(draft, originalStringId);
    onDismiss();
  };

  if (!stringInfo) {
    return null;
  }

  return (
    <Modal
      visible={visible}
      onDismiss={handleDismiss}
      header="Edit String"
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={handleDismiss}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleSave}>
              Save
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <SpaceBetween size="m">
        <ColumnLayout columns={2}>
          <FormField label="String ID" errorText={errors.stringId}>
            {isDraftAdded ? (
              <Input
                value={stringId}
                onChange={({ detail }) => {
                  setStringId(detail.value);
                  setErrors((prev) => ({ ...prev, stringId: undefined }));
                }}
              />
            ) : (
              <Input value={stringInfo.stringId} disabled />
            )}
          </FormField>
          <FormField label="Source locale">
            <Input value={sourceLocale} disabled />
          </FormField>
        </ColumnLayout>

        <FormField
          label="Source text *"
          errorText={errors.sourceText}
          constraintText={`Character limit: ${SOURCE_TEXT_CHAR_LIMIT.toLocaleString()}`}
        >
          <Textarea
            value={sourceText}
            onChange={({ detail }) => {
              setSourceText(detail.value);
              setErrors((prev) => ({ ...prev, sourceText: undefined }));
            }}
          />
        </FormField>

        <FormField
          label="Context note *"
          errorText={errors.contextNote}
          constraintText={`Character limit: ${CONTEXT_NOTE_CHAR_LIMIT.toLocaleString()}`}
        >
          <Textarea
            value={contextNote}
            rows={3}
            onChange={({ detail }) => {
              setContextNote(detail.value);
              setErrors((prev) => ({ ...prev, contextNote: undefined }));
            }}
          />
        </FormField>

        <FormField label="Inline Markup *">
          <RadioGroup
            value={inlineMarkupType}
            items={[
              { value: InlineMarkupType.PlainText, label: 'Plain Text' },
              { value: InlineMarkupType.Html, label: 'HTML' },
              { value: InlineMarkupType.Markdown, label: 'Markdown' },
            ]}
            onChange={({ detail }) =>
              setInlineMarkupType(detail.value as InlineMarkupType)
            }
          />
        </FormField>

        <FormField label="Message Format *">
          <RadioGroup
            value={messageFormatType}
            items={[
              { value: MessageFormatType.None, label: 'None' },
              { value: MessageFormatType.HashGet, label: 'Hash-Get' },
              {
                value: MessageFormatType.AmazonMessageFormat,
                label: 'Amazon Message Format',
              },
              { value: MessageFormatType.Mustache, label: 'Mustache' },
            ]}
            onChange={({ detail }) =>
              setMessageFormatType(detail.value as MessageFormatType)
            }
          />
        </FormField>
      </SpaceBetween>
    </Modal>
  );
};

export default EditStringModal;
